import type { IStock } from '@interfaces/product';

export function getDefaultStoreId(
	parameters?: {
		defaultStorehouseId?: number | null;
		defaultStoreId?: number;
	} | null
): number | undefined {
	return parameters?.defaultStorehouseId ?? parameters?.defaultStoreId;
}

/**
 * Stock key for an order: explicit depósito, else loja, else tenant defaults.
 * Prefer storeId over defaultStorehouseId so the selected loja wins in Novo pedido.
 */
export function resolveOrderStorehouseId(options: {
	storehouseId?: number | null;
	storeId?: number | null;
	parameters?: {
		defaultStorehouseId?: number | null;
		defaultStoreId?: number;
	} | null;
}): number | undefined {
	const { storehouseId, storeId, parameters } = options;
	return (
		storehouseId ??
		storeId ??
		parameters?.defaultStorehouseId ??
		parameters?.defaultStoreId ??
		undefined
	);
}

export function getTotalStock(
	stocks?: IStock[] | null,
	storeId?: number | null
): number {
	const items = stocks || [];
	const filtered =
		storeId == null ? items : items.filter(stock => stock.storeId === storeId);

	const total = filtered.reduce((sum, stock) => sum + (stock.quantity || 0), 0);

	return Math.max(0, total);
}

export function shouldShowStockToClient(
	stocks?: IStock[] | null,
	showStockToClient?: boolean
): boolean {
	return !!showStockToClient && stocks != null;
}

export function hasStockQuantityInfo(stocks?: IStock[] | null): boolean {
	return stocks != null;
}

export function isProductSellable(
	product: { available?: boolean; stocks?: IStock[] | null } | null | undefined,
	allowSellingWithoutStock?: boolean,
	storeId?: number | null
): boolean {
	if (allowSellingWithoutStock) return true;
	// When stock rows + store/depósito are known, quantity wins over `available`.
	if (product?.stocks != null && storeId != null) {
		return getTotalStock(product.stocks, storeId) > 0;
	}
	if (product?.available === true) return true;
	if (product?.available === false) return false;
	return getTotalStock(product?.stocks, storeId) > 0;
}

export function isProductOutOfStock(
	product: { available?: boolean; stocks?: IStock[] | null } | null | undefined,
	storeId?: number | null
): boolean {
	if (product?.stocks != null && storeId != null) {
		return getTotalStock(product.stocks, storeId) <= 0;
	}
	if (product?.available === true) return false;
	if (product?.available === false) return true;
	return getTotalStock(product?.stocks, storeId) <= 0;
}

/**
 * True when the product (or all variations) is marked restricted for the given loja.
 * Restriction is per stock.storeId (cadastro: "Restringir nesta unidade").
 */
export function isProductRestrictedInStore(
	product:
		| {
				stocks?: IStock[] | null;
				variations?: { stocks?: IStock[] | null }[] | null;
		  }
		| null
		| undefined,
	storeId?: number | null
): boolean {
	if (storeId == null || !product) return false;

	const stockRestricted = (stocks?: IStock[] | null) =>
		(stocks || []).some(
			stock => stock.storeId === storeId && !!stock.isRestricted
		);

	if (stockRestricted(product.stocks)) return true;

	const variations = product.variations || [];
	if (!variations.length) return false;

	// Parent without own restricted row: hide only if every variation is restricted.
	return variations.every(variation => stockRestricted(variation.stocks));
}

/**
 * Max sellable quantity in product units (same rules as isProductSellable).
 * - null: no numeric cap (allowSellingWithoutStock, or available:true without stock rows)
 * - 0: cannot sell (out of stock / available:false)
 * - number: stock quantity for the depósito/store
 */
export function resolveMaxSellableQuantity(
	product: { available?: boolean; stocks?: IStock[] | null } | null | undefined,
	options: {
		allowSellingWithoutStock?: boolean;
		storeId?: number | null;
	} = {}
): number | null {
	if (options.allowSellingWithoutStock) return null;
	if (!product) return 0;

	const storeId = options.storeId;
	// When stock rows + store/depósito are known, quantity wins over `available`.
	if (product.stocks != null && storeId != null) {
		return getTotalStock(product.stocks, storeId);
	}
	if (product.available === true) return null;
	if (product.available === false) return 0;
	return getTotalStock(product.stocks, storeId);
}
